import React, { useState, useEffect, useCallback } from 'react';
import './TrendingStocks.css';
import { API_BASE_URL } from '../../../utils/constants';
import { useCurrency } from '../../../context/CurrencyContext';

const TrendingStocks = ({ stocks }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { formatCurrency } = useCurrency();

  const getPriceChangeClass = (change) => {
    if (!change) return 'neutral';
    return change >= 0 ? 'positive' : 'negative';
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    
    const baseUrl = API_BASE_URL.replace('/api', '');
    const cleanPath = imagePath.startsWith('/') ? imagePath.slice(1) : imagePath;
    
    return `${baseUrl}/${cleanPath}`;
  };

  // FIXED: Display price properly
  const displayPrice = (stock) => {
    // Priority 1: Use backend's formatted display price
    if (stock.current_price_display) {
      return stock.current_price_display;
    }
    // Priority 2: Format current_price locally
    if (stock.current_price) {
      return formatCurrency(parseFloat(stock.current_price));
    }
    return formatCurrency(0);
  };

  const nextSlide = useCallback(() => {
    if (!stocks || stocks.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % stocks.length);
  }, [stocks]);

  useEffect(() => {
    if (!stocks || stocks.length <= 1) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 10000);

    return () => clearInterval(interval);
  }, [nextSlide, stocks]);

  if (!stocks || stocks.length === 0) {
    return (
      <div className="trending-stocks">
        <h2>Trending Stocks</h2>
        <div className="no-stocks">No trending stocks available</div>
      </div>
    );
  }

  // FIXED: Single stock should use the same carousel structure
  if (stocks.length === 1) {
    const stock = stocks[0];
    const imageUrl = getImageUrl(stock.image);
    
    return (
      <div className="trending-stocks">
        <h2>Trending</h2>
        <div className="stocks-carousel">
          <div className="carousel-track">
            <div className="carousel-slides">
              <div className="carousel-slide">
                <div className="stock-card">
                  <div className="stock-image-container">
                    {imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt={stock.symbol}
                        className="stock-image"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `<span class="stock-image-placeholder">${stock.symbol.charAt(0)}</span>`;
                        }}
                      />
                    ) : (
                      <span className="stock-image-placeholder">{stock.symbol.charAt(0)}</span>
                    )}
                  </div>
                  
                  <div className="stock-info">
                    <div className="stock-symbol">{stock.symbol}</div>
                    <div className="stock-name">{stock.name}</div>
                  </div>
                  
                  <div className="stock-price-section">
                    <div className="stock-price">{displayPrice(stock)}</div>
                    <div className={`price-change ${getPriceChangeClass(stock.price_change)}`}>
                      {stock.price_change > 0 ? '+' : ''}
                      {stock.price_change || 0}%
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="trending-stocks">
      <div className="trending-header">
        <h2>Trending</h2>
        <div className="carousel-counter">
          {currentIndex + 1} / {stocks.length}
        </div>
      </div>

      <div className="stocks-carousel">
        <div className="carousel-track">
          <div 
            className="carousel-slides"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {stocks.map((stock) => {
              const imageUrl = getImageUrl(stock.image);
              
              return (
                <div key={stock.id} className="carousel-slide">
                  <div className="stock-card">
                    <div className="stock-image-container">
                      {imageUrl ? (
                        <img 
                          src={imageUrl} 
                          alt={stock.symbol}
                          className="stock-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = `<span class="stock-image-placeholder">${stock.symbol.charAt(0)}</span>`;
                          }}
                        />
                      ) : (
                        <span className="stock-image-placeholder">{stock.symbol.charAt(0)}</span>
                      )}
                    </div>
                    
                    <div className="stock-info">
                      <div className="stock-symbol">{stock.symbol}</div>
                      <div className="stock-name">{stock.name}</div>
                    </div>
                    
                    <div className="stock-price-section">
                      <div className="stock-price">{displayPrice(stock)}</div>
                      <div className={`price-change ${getPriceChangeClass(stock.price_change)}`}>
                        {stock.price_change > 0 ? '+' : ''}
                        {stock.price_change || 0}%
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TrendingStocks;